import { useState } from 'react';
import {
  Button,
  Card,
  Group,
  List,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { Plus } from 'react-feather';
import {
  parseEmails,
  plural,
  resolveDoNotMatchGroups,
} from '../../model/derive';
import { useSetup } from '../../SetupContext';
import { SessionOnlyNote } from './SessionOnlyNote';

/**
 * One list's textarea. It keeps the raw text while typing (so a trailing
 * comma isn't swallowed) and saves the parsed emails on every change.
 */
function GroupEditor({ index }: { index: number }) {
  const { config, update, readOnly } = useSetup();
  const group = config.doNotMatchGroups[index];
  const [text, setText] = useState(group.emails.join(', '));

  return (
    <Card withBorder radius="md" p="md">
      <Group gap="sm" mb="sm" wrap="nowrap">
        <TextInput
          aria-label="List name"
          fw={600}
          style={{ flex: 1 }}
          value={group.name}
          readOnly={readOnly}
          onChange={e =>
            update({
              type: 'setDoNotMatchGroup',
              index,
              group: { ...group, name: e.currentTarget.value },
            })
          }
        />
        {!readOnly && config.doNotMatchGroups.length > 1 && (
          <Button
            variant="default"
            onClick={() => update({ type: 'removeDoNotMatchGroup', index })}
          >
            Remove list
          </Button>
        )}
      </Group>
      <Textarea
        aria-label="Emails in this list"
        autosize
        minRows={5}
        value={text}
        readOnly={readOnly}
        onChange={e => {
          const value = e.currentTarget.value;
          setText(value);
          update({
            type: 'setDoNotMatchGroup',
            index,
            group: { ...group, emails: parseEmails(value) },
          });
        }}
      />
      <Text size="sm" c="dimmed" mt="xs">
        {plural(group.emails.length, 'email')}
      </Text>
    </Card>
  );
}

export function DnmGroupsTab() {
  const { session, config, update, readOnly } = useSetup();
  const resolved = resolveDoNotMatchGroups(
    config.doNotMatchGroups,
    session.participants
  );
  const lists = config.doNotMatchGroups.length;
  const summary = `${resolved.totalEmails} emails in ${plural(lists, 'list')}. ${
    resolved.notFound.length === 0
      ? 'All matched.'
      : `${resolved.notFound.length} not found.`
  }`;

  return (
    <SimpleGrid cols={{ base: 1, md: 2 }} spacing={40}>
      <Stack gap="md">
        <div>
          <Title order={4}>Do-not-match groups</Title>
          <Text size="sm" c="dimmed" mt={6}>
            Each list is a group of people who should never be in the same
            group. Paste emails separated by commas. Lists stay on this tab and
            don't fill the participant table. Use the Do Not Match column there
            for one-to-one matches.
          </Text>
        </div>
        <SessionOnlyNote>
          These lists are used for this matching session only. They aren't saved
          to SGOS, so update SGOS too if they should stick.
        </SessionOnlyNote>
        {config.doNotMatchGroups.map((_, i) => (
          // Lists have no id; the index is their identity until one is removed.
          <GroupEditor key={`${i}-${lists}`} index={i} />
        ))}
        {!readOnly && (
          <Button
            variant="outline"
            leftSection={<Plus size={16} />}
            style={{ alignSelf: 'flex-start', borderStyle: 'dashed' }}
            onClick={() => update({ type: 'addDoNotMatchGroup' })}
          >
            Add a list
          </Button>
        )}
      </Stack>
      <Stack gap="sm">
        <Title order={4}>Emails not found</Title>
        <Text size="sm" c="dimmed">
          {summary}
        </Text>
        {resolved.notFound.length === 0 ? (
          <Paper bg="medleyGreen.0" p="sm" radius="md">
            <Text size="sm">Every email matched a participant.</Text>
          </Paper>
        ) : (
          <List spacing="xs" listStyleType="none">
            {resolved.notFound.map(d => (
              <List.Item key={`${d.list}-${d.email}`}>
                <Paper
                  bg="dangerRed.0"
                  p="sm"
                  radius="md"
                  style={{
                    border: '1px solid var(--mantine-color-dangerRed-5)',
                    borderLeftWidth: 6,
                  }}
                >
                  <Text
                    size="sm"
                    fw={600}
                    c="dangerRed.9"
                    style={{ overflowWrap: 'anywhere' }}
                  >
                    {d.email}
                  </Text>
                  <Text size="xs" c="dangerRed.9">
                    In {d.list}. No participant found.
                  </Text>
                </Paper>
              </List.Item>
            ))}
          </List>
        )}
        <Text size="xs" c="dimmed">
          Emails with no participant are skipped. Fix the spelling or remove
          them.
        </Text>
      </Stack>
    </SimpleGrid>
  );
}
